import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import {
  CATEGORY_LABELS, CAUSE_LABELS, IMPACT_LABELS, PRIORITIES, PRIORITY_LABELS, URGENCY_LABELS, computedPriority, ticketErrorMessage,
} from "../../lib/tickets";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import PharmacyPicker from "./PharmacyPicker";

const NONE = "__none__";

function useRequest() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (request, onDone) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      onDone(data);
    } catch (e) {
      setError(ticketErrorMessage(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function Footer({ busy, error, onCancel, onSubmit, disabled, label, danger, testid }) {
  return (
    <>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="ticket-dialog-error">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button variant={danger ? "destructive" : "default"} onClick={onSubmit} disabled={busy || disabled} data-testid={testid}>
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
        </Button>
      </DialogFooter>
    </>
  );
}

// --- Status (D-11): a reason for a wait, a reopening or « clore sans suite », a solution (and an optional cause) to resolve -------
export function StatusDialog({ ticket, move, onOpenChange, onDone }) {
  const req = useRequest();
  const [reason, setReason] = useState("");
  const [solution, setSolution] = useState("");
  const [cause, setCause] = useState(NONE);
  useEffect(() => { if (move) { setReason(""); setSolution(""); setCause(NONE); req.setError(""); } }, [move]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ticket || !move) return null;
  const valid = move.needs === "reason" ? reason.trim().length >= 3 : move.needs === "solution" ? solution.trim().length >= 3 : true;
  const submit = () => {
    const body = { expected_version: ticket.version, status: move.to };
    if (move.needs === "reason") body.reason = reason.trim();
    if (move.needs === "solution") { body.solution = solution.trim(); if (cause !== NONE) body.cause = cause; }
    req.run(() => api.post(`/tickets/${ticket.id}/status`, body), onDone);
  };
  const reasonLabel = move.to === "in_progress" ? "Pourquoi le rouvrir ?" : move.to === "closed" ? "Pourquoi le clore sans suite ?" : "Qu'attendez-vous ?";
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="ticket-status-dialog">
        <DialogHeader>
          <DialogTitle>{move.label}</DialogTitle>
          <DialogDescription>{ticket.number} · {ticket.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {move.needs === "reason" && (
            <div className="space-y-1.5">
              <Label htmlFor="ticket-status-reason">{reasonLabel}</Label>
              <Textarea id="ticket-status-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
                placeholder={move.to === "closed" ? "Doublon, demande hors sujet, erreur de saisie…" : ""} data-testid="ticket-status-reason" />
            </div>
          )}
          {move.needs === "solution" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ticket-status-solution">Solution apportée</Label>
                <Textarea id="ticket-status-solution" value={solution} onChange={(e) => setSolution(e.target.value)} rows={4} maxLength={5000}
                  placeholder="Ce qui a été fait ou expliqué à la pharmacie" data-testid="ticket-status-solution" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ticket-status-cause">Cause (facultative)</Label>
                <Select value={cause} onValueChange={setCause}>
                  <SelectTrigger id="ticket-status-cause" data-testid="ticket-status-cause"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Non précisée</SelectItem>
                    {Object.entries(CAUSE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {!move.needs && <p className="text-sm text-slate-600">Confirmez le changement de statut.</p>}
        </div>
        <Footer {...req} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!valid} label={move.label} danger={move.danger}
          testid="ticket-status-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Responsible (D-32): the server's candidates; without « tous », oneself only -------------------------------------------------
export function AssignDialog({ ticket, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [target, setTarget] = useState("");
  useEffect(() => { if (open) { setTarget(ticket?.responsible_user_id || ""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ticket) return null;
  const candidates = ticket.responsible_candidates || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ticket-assign-dialog">
        <DialogHeader>
          <DialogTitle>Changer le responsable</DialogTitle>
          <DialogDescription>Le responsable traite le ticket ; il est prévenu par WhatsApp.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="ticket-assign">Responsable</Label>
          <Select value={target || NONE} onValueChange={(v) => setTarget(v === NONE ? "" : v)}>
            <SelectTrigger id="ticket-assign" data-testid="ticket-assign-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>
              {ticket.status === "new" && <SelectItem value={NONE}>Aucun responsable</SelectItem>}
              {candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Footer {...req} onCancel={() => onOpenChange(false)} label="Enregistrer" testid="ticket-assign-submit"
          disabled={(target || null) === (ticket.responsible_user_id || null) || (!target && ticket.status !== "new")}
          onSubmit={() => req.run(() => api.post(`/tickets/${ticket.id}/assign`, { expected_version: ticket.version, responsible_user_id: target || null }), onDone)} />
      </DialogContent>
    </Dialog>
  );
}

// --- Priority (D-14): adjusted with a reason, or back to the computed one ------------------------------------------------------------
export function PriorityDialog({ ticket, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [priority, setPriority] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setPriority(ticket?.priority || ""); setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ticket) return null;
  const lifting = priority === "computed";
  const valid = lifting || (priority && priority !== ticket.priority && reason.trim().length >= 3);
  const submit = () => req.run(() => api.post(`/tickets/${ticket.id}/priority`, lifting
    ? { expected_version: ticket.version, priority: null, ...(reason.trim() ? { reason: reason.trim() } : {}) }
    : { expected_version: ticket.version, priority, reason: reason.trim() }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ticket-priority-dialog">
        <DialogHeader>
          <DialogTitle>Ajuster la priorité</DialogTitle>
          <DialogDescription>Priorité calculée : {PRIORITY_LABELS[ticket.priority_computed]}. Les délais suivent la priorité retenue.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-priority">Priorité</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="ticket-priority" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}
                {ticket.priority_adjusted && <SelectItem value="computed">Revenir à la priorité calculée</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-priority-reason">Motif{lifting ? " (facultatif)" : ""}</Label>
            <Textarea id="ticket-priority-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
              data-testid="ticket-priority-reason" />
          </div>
        </div>
        <Footer {...req} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!valid} label="Enregistrer" testid="ticket-priority-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Editing the ticket itself (titre, description, catégorie, impact, urgence, demandeur) ---------------------------------------
export function EditTicketDialog({ ticket, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open && ticket) {
      setForm({ title: ticket.title, description: ticket.description || "", category: ticket.category, impact: ticket.impact, urgency: ticket.urgency,
                requesterName: ticket.requester?.name || "", requesterRole: ticket.requester?.role || "", requesterPhone: ticket.requester?.phone || "" });
      req.setError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ticket) return null;
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value?.target ? value.target.value : value }));
  const submit = () => {
    const body = { expected_version: ticket.version };
    for (const key of ["title", "description", "category", "impact", "urgency"]) if (form[key] !== (ticket[key] ?? "")) body[key] = form[key];
    const requester = { name: form.requesterName.trim(), role: form.requesterRole.trim() || null, phone: form.requesterPhone.trim() || null };
    const before = ticket.requester || {};
    if (requester.name !== before.name || requester.role !== (before.role || null) || requester.phone !== (before.phone || null)) body.requester = requester;
    req.run(() => api.patch(`/tickets/${ticket.id}`, body), onDone);
  };
  const computed = computedPriority(form.impact, form.urgency);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ticket-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier le ticket</DialogTitle>
          <DialogDescription>{ticket.number} · aucune donnée patient ni ordonnance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-edit-title">Titre</Label>
            <Input id="ticket-edit-title" value={form.title || ""} onChange={set("title")} maxLength={120} data-testid="ticket-edit-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-edit-description">Description</Label>
            <Textarea id="ticket-edit-description" value={form.description || ""} onChange={set("description")} rows={4} maxLength={5000} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <SelectField id="ticket-edit-category" label="Catégorie" value={form.category} onChange={set("category")} options={CATEGORY_LABELS} />
            <SelectField id="ticket-edit-impact" label="Impact" value={form.impact} onChange={set("impact")} options={IMPACT_LABELS} />
            <SelectField id="ticket-edit-urgency" label="Urgence" value={form.urgency} onChange={set("urgency")} options={URGENCY_LABELS} />
          </div>
          {computed && <p className="text-xs text-slate-500">Priorité calculée : {PRIORITY_LABELS[computed]}{ticket.priority_adjusted ? " (l'ajustement manuel reste en place)" : ""}</p>}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="ticket-edit-requester">Demandeur</Label>
              <Input id="ticket-edit-requester" value={form.requesterName || ""} onChange={set("requesterName")} maxLength={120} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-edit-requester-role">Fonction</Label>
              <Input id="ticket-edit-requester-role" value={form.requesterRole || ""} onChange={set("requesterRole")} maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-edit-requester-phone">Rappel</Label>
              <Input id="ticket-edit-requester-phone" value={form.requesterPhone || ""} onChange={set("requesterPhone")} maxLength={40} inputMode="tel" />
            </div>
          </div>
        </div>
        <Footer {...req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Enregistrer" testid="ticket-edit-submit"
          disabled={(form.title || "").trim().length < 3 || (form.requesterName || "").trim().length < 2} />
      </DialogContent>
    </Dialog>
  );
}

// 08-4 « Rattacher à une pharmacie »: a request received from Jamora names Jamora's pharmacy, never a CRM fiche; the suggestion
// comes from an OPEN customer-account tenant link, which is only declared — a person confirms it, or picks another pharmacy.
export function AttachPharmacyDialog({ ticket, open, onOpenChange, onDone }) {
  const req = useRequest();
  const suggested = ticket?.pharmacy_pending?.suggested || null;
  const [pharmacy, setPharmacy] = useState(null);
  useEffect(() => { if (open) { setPharmacy(suggested); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ticket) return null;
  const submit = () => req.run(() => api.post(`/tickets/${ticket.id}/attach-pharmacy`, { expected_version: ticket.version, prospect_id: pharmacy.id }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Rattacher à une pharmacie</DialogTitle>
          <DialogDescription>
            Demande envoyée depuis Jamora par « {ticket.pharmacy_pending?.display_name} ». Choisissez sa fiche dans le CRM.
          </DialogDescription>
        </DialogHeader>
        {suggested && (
          <button type="button" onClick={() => setPharmacy(suggested)} aria-pressed={pharmacy?.id === suggested.id} data-testid="ticket-attach-suggested"
            className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${pharmacy?.id === suggested.id ? "border-jamora bg-jamora/5" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="font-medium text-slate-900">{suggested.name}</div>
            <div className="text-xs text-slate-500">Suggérée : son dossier client est lié à cette pharmacie Jamora (liaison déclarée, à confirmer).</div>
          </button>
        )}
        <div className="space-y-1.5">
          <Label>{suggested ? "Ou une autre pharmacie" : "Pharmacie"}</Label>
          <PharmacyPicker value={pharmacy && pharmacy.id !== suggested?.id ? pharmacy : null} onChange={setPharmacy} testid="ticket-attach-pharmacy" />
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!pharmacy}
          label="Rattacher" testid="ticket-attach-submit" />
      </DialogContent>
    </Dialog>
  );
}

export function SelectField({ id, label, value, onChange, options, placeholder = "Choisir", testid }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={testid || id}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
